/**
 * Newsletter pop-up (THEME.md §7.24; D-135 NL1–NL11, T-218): the element of `sections/okt-newsletter-popup.liquid`.
 * It is Horizon's dialog (it extends `DialogComponent`, so Escape, a click outside, the scroll lock, the closing
 * animation and the focus's return are Horizon's for the modal displays).
 *
 * - Display: from 750 px `data-display`, below it `data-display-mobile`: popup, drawer_right and drawer_left open as
 *   Horizon's modal dialog; sheet opens by its `open` attribute, not modal, so the page stays usable and keeps the
 *   focus, and Escape closes it wherever the focus is, unless another overlay is open (with `data-sheet-dim` it is
 *   modal too).
 * - Its turn (NL4), after the load event: once cookie consent is settled, and once the store suggestion is done.
 *   Consent: Shopify's Customer Privacy API (loaded as Shopify documents it) says whether this visitor still has to
 *   answer (shouldShowBanner() and currentVisitorConsent()), and a banner on the screen counts too: Shopify's
 *   (#shopify-pc__banner, #shopify-pc__prefs) or a theme banner's elements marked `data-okt-consent-banner`; Shopify's
 *   `visitorConsentCollected` ends the wait. The store suggestion takes `data-done` and dispatches
 *   `okt-store-suggestion:done` when it has decided not to show or has been closed.
 * - Triggers (NL3), counted from then: Delay, Scroll depth (Horizon's scroll container) and Exit intent (with a mouse
 *   only; set alone, nothing opens on touch screens); Open on the first, or once all have happened. Thresholds: From
 *   page view and Time on site, counted by the section's inline script.
 * - Never in the way: it waits while another dialog (a drawer, search, quick add), the menu drawer, a mega menu panel
 *   or a cookie banner is open, a product card's sizes show, a field has focus or the tab is hidden. Never for crawlers
 *   and automated browsers.
 * - Frequency (NL5): Days before showing again after a close, once per visit, never after a sign-up from any
 *   newsletter form or a visit from the store's emails. In the browser only: localStorage `okt-newsletter-popup`
 *   ({ at, action: close | signup | email }) and sessionStorage `okt-newsletter-popup` ({ shown, views, start,
 *   teaser }), read and written in try/catch.
 * - After a sign-up in this pop-up (NL9) Shopify returns to the page at its form's anchor with customer_posted=true:
 *   the anchor leaves the address and the pop-up opens on Thanks, once; after an error, on Sign-up with Horizon's error
 *   under the field.
 * - Teaser (NL11, `data-teaser`): after closing, for the rest of the visit; a click reopens the pop-up, its X hides it
 *   for the set days.
 * - Theme editor: nothing opens by itself; it shows while the section (Sign-up) or a block in it (that block's zone)
 *   is selected, and nothing is stored.
 */
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { onAnimationEnd, requestIdleCallback } from '@theme/utilities';
import { getScrollContainer } from '@theme/scroll-container';

const KEY = 'okt-newsletter-popup';
const DAY = 86400000;
const POLL = 1000;
const BOTS = /bot|crawl|spider|slurp|lighthouse|headless|facebookexternalhit|mediapartners/i;
const CONSENT = '#shopify-pc__banner, #shopify-pc__prefs, [data-okt-consent-banner]';
/** An open mega menu panel (Horizon keeps the closed ones inert) or the More menu (THEME.md §7.24, T-234) */
const MEGA_MENU = '.menu-list__submenu:not([inert]), header-menu[data-overflow-expanded="true"]';
const EDITABLE =
  'input:not([type="hidden"], [type="checkbox"], [type="radio"], [type="button"], [type="submit"]), textarea, select, [contenteditable]:not([contenteditable="false"])';
const HEADING = 'h1, h2, h3, h4, h5, h6';
/** An open popover, which Escape closes by itself (Horizon's polyfill marks one with a class) */
const POPOVER = 'popover' in HTMLElement.prototype ? '[popover]:popover-open' : '[popover].\\:popover-open';
const desktop = matchMedia('(min-width: 750px)');
const mouse = matchMedia('(hover: hover) and (pointer: fine)');

/** Sections selected in the theme editor, with the zone they show, so a section rendered again shows again */
const selected = new Map();

/** @param {() => Storage} area */
const storage = (area) => ({
  read() {
    try {
      return JSON.parse(area().getItem(KEY) ?? 'null');
    } catch {
      return null;
    }
  },
  /** @param {object} value */
  write(value) {
    try {
      area().setItem(KEY, JSON.stringify(value));
    } catch {
      // Storage is blocked: it may show again on a later page
    }
  },
});

const local = storage(() => localStorage);
const session = storage(() => sessionStorage);

/** @param {object} changes */
const updateVisit = (changes) => session.write({ ...(session.read() ?? {}), ...changes });

/** @param {{ action?: string } | null} record */
const isForGood = (record) => record?.action === 'signup' || record?.action === 'email';

const isBot = () => navigator.webdriver === true || BOTS.test(navigator.userAgent);

/**
 * @param {number} ms
 * @param {AbortSignal} signal
 */
const wait = (ms, signal) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(signal.reason);
      },
      { once: true }
    );
  });

/** The page's load event */
const loaded = new Promise((resolve) => {
  if (document.readyState === 'complete') resolve(undefined);
  else window.addEventListener('load', () => resolve(undefined), { once: true });
});

/**
 * A product card's sizes while they show (Product cards › Size picker › On the card, THEME.md §7.6; T-228): opened by
 * the "+", hovered or focused
 */
function sizesShowing() {
  for (const band of document.querySelectorAll('.okt-quick-add-sizes')) {
    if (getComputedStyle(band).visibility === 'visible') return true;
  }
  return false;
}

/** A cookie banner or its preferences on the screen */
function consentShowing() {
  for (const element of document.querySelectorAll(CONSENT)) {
    if (element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden') return true;
  }
  return false;
}

/** Shopify's Customer Privacy API, loaded as Shopify documents it; null without it, after 5 s at most */
function privacyApi() {
  const shopify = /** @type {any} */ (window).Shopify;
  if (shopify?.customerPrivacy) return Promise.resolve(shopify.customerPrivacy);
  if (typeof shopify?.loadFeatures !== 'function') return Promise.resolve(null);

  return new Promise((resolve) => {
    const done = () => resolve(/** @type {any} */ (window).Shopify?.customerPrivacy ?? null);
    const timer = setTimeout(done, 5000);
    try {
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], () => {
        clearTimeout(timer);
        done();
      });
    } catch {
      clearTimeout(timer);
      done();
    }
  });
}

/**
 * A visitor who still has to answer: a banner on the screen, or a consent region without an answer yet
 * @param {any} api
 */
function consentPending(api) {
  if (consentShowing()) return true;
  try {
    if (!api || api.shouldShowBanner?.() !== true) return false;
    const consent = api.currentVisitorConsent?.() ?? {};
    return !['analytics', 'marketing', 'preferences'].some((purpose) => consent[purpose] === 'yes' || consent[purpose] === 'no');
  } catch {
    return false;
  }
}

/** @param {AbortSignal} signal */
async function consentSettled(signal) {
  let answered = false;
  const answer = new Promise((resolve) => {
    document.addEventListener('visitorConsentCollected', () => resolve((answered = true)), { once: true, signal });
  });
  const api = await privacyApi();
  while (!answered && consentPending(api)) await Promise.race([wait(POLL, signal), answer]);
}

/** @param {AbortSignal} signal */
function suggestionDone(signal) {
  const suggestion = document.querySelector('okt-store-suggestion');
  if (!suggestion?.hasAttribute('data-enabled') || suggestion.hasAttribute('data-done')) return Promise.resolve();

  return new Promise((resolve) => {
    suggestion.addEventListener('okt-store-suggestion:done', resolve, { once: true, signal });
  });
}

/**
 * Another overlay is open: a dialog (a drawer, search, quick add, the store suggestion, the cookie banner), the menu
 * drawer, a mega menu panel, a product card's sizes or a cookie banner
 * @param {HTMLDialogElement} own
 */
function overlayOpen(own) {
  if ([...document.querySelectorAll('dialog[open]')].some((dialog) => dialog !== own)) return true;
  if (document.querySelector('details[open][scroll-lock]')) return true;
  if (document.querySelector(MEGA_MENU) || sizesShowing()) return true;
  return consentShowing();
}

/** @param {HTMLDialogElement} own */
function isBusy(own) {
  if (document.visibilityState !== 'visible' || overlayOpen(own)) return true;

  const active = document.activeElement;
  return Boolean(active?.matches(EDITABLE) || active?.closest('localization-form-component'));
}

/**
 * Resolves once the visitor has scrolled `depth` % of the page, in Horizon's scroll container (from 990 px the page
 * wrapper, else the document)
 * @param {number} depth
 * @param {AbortSignal} signal
 */
function scrolled(depth, signal) {
  return new Promise((resolve) => {
    const controller = new AbortController();
    signal.addEventListener('abort', () => controller.abort(), { once: true });
    let frame = 0;

    const check = () => {
      frame = 0;
      const container = getScrollContainer();
      const range = container.scrollHeight - container.clientHeight;
      if (range > 0 && (container.scrollTop / range) * 100 < depth) return;
      controller.abort();
      resolve(undefined);
    };

    document.addEventListener(
      'scroll',
      () => {
        frame ||= requestAnimationFrame(check);
      },
      { capture: true, passive: true, signal: controller.signal }
    );
    check();
  });
}

/**
 * Resolves when the pointer leaves the page towards the tabs or the address bar
 * @param {AbortSignal} signal
 */
function exitIntent(signal) {
  return new Promise((resolve) => {
    const controller = new AbortController();
    signal.addEventListener('abort', () => controller.abort(), { once: true });
    document.addEventListener(
      'mouseout',
      (event) => {
        if (event.relatedTarget || event.clientY > 0) return;
        controller.abort();
        resolve(undefined);
      },
      { signal: controller.signal }
    );
  });
}

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 *
 * @extends {DialogComponent}
 */
class OktNewsletterPopup extends DialogComponent {
  /** @type {AbortController | undefined} */
  #abortController;

  /** The zone shown: signup or thanks */
  #zone = 'signup';

  /** Opened from the teaser, which takes the focus back when it closes */
  #fromTeaser = false;

  /** @type {ResizeObserver | undefined} */
  #sheetObserver;

  /**
   * The sheet's Escape listener on the page, while it's open
   * @type {AbortController | undefined}
   */
  #sheetListeners;

  /** @type {number | undefined} */
  #closeTimer;

  /** Horizon's close, for the modal displays */
  #closeModal = this.closeDialog;

  /** Closes the dialog: Horizon's close for the modal displays; for the sheet, its closing animation without the scroll lock's restore */
  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;
    if (dialog.matches(':modal')) return this.#closeModal();

    // As Horizon's close: the key listener goes first
    this.#sheetListeners?.abort();
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';
    await onAnimationEnd(dialog, undefined, { subtree: false });

    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.dispatchEvent(new DialogCloseEvent());
  };

  get #designMode() {
    return this.hasAttribute('data-design-mode');
  }

  /** The display at this width: popup, drawer_right, drawer_left or sheet */
  get #display() {
    return (desktop.matches ? this.dataset.display : this.dataset.displayMobile) || 'popup';
  }

  get #modal() {
    return this.#display !== 'sheet' || this.hasAttribute('data-sheet-dim');
  }

  get #teaser() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-okt-teaser]'));
  }

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener(DialogOpenEvent.eventName, this.#onOpen, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#onClose, { signal });

    if (this.#designMode) {
      document.addEventListener('shopify:section:select', this.#onSelect, { signal });
      document.addEventListener('shopify:block:select', this.#onSelect, { signal });
      document.addEventListener('shopify:section:deselect', this.#onDeselect, { signal });
      document.addEventListener('shopify:block:deselect', this.#onDeselect, { signal });
      const zone = selected.get(this.dataset.sectionId);
      if (zone) this.#open(zone);
      return;
    }

    if (!this.hasAttribute('data-enabled') || document.querySelector('okt-newsletter-popup') !== this) return;
    if (this.#returned(signal)) return;

    const record = local.read();
    if (isForGood(record)) return;
    if (this.hasAttribute('data-teaser') && session.read()?.teaser === true) this.#showTeaser();
    if (!this.#resting(record)) this.#start(signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#sheetListeners?.abort();
    this.#sheetObserver?.disconnect();
  }

  /**
   * Kept away on this page: signed up, from the store's emails, closed within the days, shown in this visit, or before
   * the visit's page threshold
   * @param {{ at?: number, action?: string } | null} [record]
   */
  #resting(record = local.read()) {
    if (isForGood(record)) return true;

    const days = Number(this.dataset.days) || 7;
    if (record?.action === 'close' && typeof record.at === 'number' && Date.now() - record.at < days * DAY) return true;

    const visit = session.read() ?? {};
    if (this.hasAttribute('data-once') && visit.shown) return true;

    const views = Number(this.dataset.views) || 1;
    return views > 1 && (Number(visit.views) || 1) < views;
  }

  /** @param {AbortSignal} signal */
  async #start(signal) {
    if (isBot()) return;

    try {
      await loaded;
      await new Promise((resolve) => requestIdleCallback(resolve));
      await consentSettled(signal);
      await suggestionDone(signal);

      // The way is clear: the triggers count from now
      if (!(await this.#triggered(performance.now(), signal))) return;

      const seconds = Number(this.dataset.time) || 0;
      const start = session.read()?.start;
      if (seconds > 0 && typeof start === 'number') await wait(Math.max(0, start + seconds * 1000 - Date.now()), signal);

      while (isBusy(this.refs.dialog)) await wait(POLL, signal);
      if (!this.#resting()) this.#open('signup');
    } catch {
      // The page is leaving
    }
  }

  /**
   * Waits for the triggers set: the first, or all of them. False when the only one set can't happen here (Exit intent
   * without a mouse).
   * @param {number} from - when the way became clear
   * @param {AbortSignal} signal
   */
  async #triggered(from, signal) {
    const triggers = [];
    const delay = Number(this.dataset.delay) || 0;
    const depth = Number(this.dataset.scroll) || 0;
    const exit = this.hasAttribute('data-exit');

    if (delay > 0) triggers.push(wait(Math.max(0, from + delay * 1000 - performance.now()), signal));
    if (depth > 0) triggers.push(scrolled(depth, signal));
    if (exit && mouse.matches) triggers.push(exitIntent(signal));
    if (!triggers.length) return !exit;

    await (this.dataset.logic === 'all' ? Promise.all(triggers) : Promise.race(triggers));
    return true;
  }

  /**
   * After a sign-up in this pop-up, Shopify returns at its form's anchor with Horizon's thanks or error in the form:
   * opens on Thanks once (Sign-up when Thanks is empty, with Horizon's thanks), or on Sign-up with the error
   * @param {AbortSignal} signal
   */
  #returned(signal) {
    const form = [...this.querySelectorAll('form[id]')].find((element) => location.hash === `#${element.id}`);
    if (!form?.querySelector('.email-signup__message')) return false;

    // The anchor points into the pop-up: once it shows, the browser would move the focus out of it to the anchor
    history.replaceState(history.state, '', `${location.pathname}${location.search}`);

    const thanks = Boolean(form.querySelector('[id^="Email-signup__message-success-"]'));
    if (thanks) local.write({ at: Date.now(), action: 'signup' });

    (async () => {
      try {
        await loaded;
        while (isBusy(this.refs.dialog)) await wait(POLL, signal);
        this.#open(thanks ? 'thanks' : 'signup');
      } catch {
        // The page is leaving
      }
    })();

    return true;
  }

  /** @param {string} zone */
  #open(zone) {
    const { dialog } = this.refs;

    this.#show(zone);
    dialog.classList.toggle('okt-newsletter__dialog--drawer', this.#display.startsWith('drawer'));
    if (!this.#designMode) updateVisit({ shown: true });
    if (dialog.open) return;

    if (this.#modal) {
      this.showDialog();
      return;
    }

    // The sheet isn't modal: the page keeps the focus and its place in the tab order. show() would move the focus into
    // the dialog (its focusing steps), so the sheet opens by its attribute, as a non-modal dialog does without them.
    dialog.setAttribute('open', '');
    this.dispatchEvent(new DialogOpenEvent());
  }

  /**
   * Shows one zone (Sign-up when the other renders nothing) and names the dialog by its first heading, else by the
   * fallback name
   * @param {string} zone
   */
  #show(zone) {
    const { dialog } = this.refs;
    const zones = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-okt-zone]')]);
    const shown = zones.find((element) => element.dataset.oktZone === zone) ?? zones.find((element) => element.dataset.oktZone === 'signup');

    for (const element of zones) element.hidden = element !== shown;
    this.#zone = shown?.dataset.oktZone ?? 'signup';

    for (const element of this.querySelectorAll('[data-okt-title]')) element.removeAttribute('data-okt-title');
    const heading = shown?.querySelector(HEADING);

    if (heading instanceof HTMLElement) {
      heading.id ||= `OktNewsletterTitle-${this.dataset.sectionId}-${this.#zone}`;
      heading.setAttribute('data-okt-title', '');
      if (!heading.hasAttribute('tabindex')) heading.tabIndex = -1;
      dialog.setAttribute('aria-labelledby', heading.id);
      dialog.removeAttribute('aria-label');
    } else {
      dialog.removeAttribute('aria-labelledby');
      dialog.setAttribute('aria-label', this.dataset.label ?? '');
    }
  }

  #onOpen = () => {
    const { dialog } = this.refs;
    this.#hideTeaser();

    if (dialog.matches(':modal')) {
      // The title takes the focus, not the field: no keyboard pops up on phones; a field with an error keeps it
      const target = dialog.querySelector('[aria-invalid="true"]') ?? dialog.querySelector('[data-okt-title]');
      if (target instanceof HTMLElement) target.focus({ preventScroll: true });
      return;
    }

    // The sheet: the page scrolls focused controls clear of it, and Escape closes it wherever the focus is
    const root = document.documentElement;
    this.#sheetObserver?.disconnect();
    this.#sheetObserver = new ResizeObserver(() => {
      root.style.setProperty('--okt-newsletter-sheet-height', `${dialog.offsetHeight}px`);
    });
    this.#sheetObserver.observe(dialog);
    this.#sheetListeners?.abort();
    this.#sheetListeners = new AbortController();
    document.addEventListener('keydown', this.#onSheetKeydown, { signal: this.#sheetListeners.signal });
  };

  #onClose = () => {
    this.#sheetObserver?.disconnect();
    document.documentElement.style.removeProperty('--okt-newsletter-sheet-height');

    if (this.#designMode || this.#zone === 'thanks') return;

    if (!isForGood(local.read())) local.write({ at: Date.now(), action: 'close' });

    if (this.hasAttribute('data-teaser')) {
      updateVisit({ teaser: true });
      this.#showTeaser();
      if (this.#fromTeaser && (!document.activeElement || document.activeElement === document.body)) {
        this.#teaser?.querySelector('button')?.focus();
      }
    }
    this.#fromTeaser = false;
  };

  /**
   * The sheet isn't modal, so the focus may be anywhere: Escape closes it as its X does, unless something on the page
   * took the key first, or another overlay or a popover is open (the key is that one's)
   */
  #onSheetKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key !== 'Escape' || event.isComposing || event.defaultPrevented) return;
    if (overlayOpen(this.refs.dialog) || document.querySelector(POPOVER)) return;

    event.preventDefault();
    this.closeDialog();
  };

  #showTeaser() {
    const teaser = this.#teaser;
    if (teaser) teaser.hidden = false;
  }

  #hideTeaser() {
    const teaser = this.#teaser;
    if (teaser) teaser.hidden = true;
  }

  /** The teaser's button: opens the pop-up on Sign-up */
  reopen = () => {
    this.#fromTeaser = true;
    this.#open('signup');
  };

  /** The teaser's X: hides it for the rest of the visit and, with the pop-up, for the set days */
  hideTeaser = () => {
    this.#hideTeaser();
    updateVisit({ teaser: false });
    if (!isForGood(local.read())) local.write({ at: Date.now(), action: 'close' });
  };

  /** @param {Event} event */
  #isMine(event) {
    return event.target instanceof Element && (event.target.contains(this) || this.contains(event.target));
  }

  #onSelect = (/** @type {Event} */ event) => {
    if (!this.#isMine(event)) return;

    const target = /** @type {Element} */ (event.target);
    const zone = event.type === 'shopify:block:select' && target.closest('[data-okt-zone="thanks"]') ? 'thanks' : 'signup';
    selected.set(this.dataset.sectionId, zone);
    clearTimeout(this.#closeTimer);
    this.#open(zone);
  };

  // Moving from the section to a block, or between blocks, deselects and selects at once: it closes only when no new
  // selection follows
  #onDeselect = (/** @type {Event} */ event) => {
    if (!this.#isMine(event)) return;

    selected.delete(this.dataset.sectionId);
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      if (!selected.has(this.dataset.sectionId)) this.closeDialog();
    }, 100);
  };
}

if (!customElements.get('okt-newsletter-popup')) customElements.define('okt-newsletter-popup', OktNewsletterPopup);
